/**
 * @file functions/AddToCart.ts
 *
 * Adds a product to the user's Sylius cart via the local
 * `/api/orders/[token]/items` route. Handles one retry for
 * transient errors and refreshes the cart token when it is stale.
 */

import { getSession } from 'next-auth/react';

// Context
import { updateOrder } from '@/lib/context/orderActions';

// Functions
import { addAuthorizationHeader } from '@/lib/functions/addAuthorizationHeader';
import { convertToOrderState } from '@/lib/functions/convertToOrderState';
import { getOrderToken } from '@/lib/functions/getOrderToken';
import { isSyliusOrderResponse } from '@/lib/functions/isSyliusOrderResponse';
import { logMessage } from '@/lib/functions/logMessage';
import { publishCartRefresh } from '@/lib/broadcast/cartSync';

// Types
import type { OrderActions } from '@/lib/context/orderActions';
import type { OrderState as OrderStateType } from '@/lib/types/orderState';
import type { SyliusOrderResponse } from '@/lib/types/syliusOrderResponse';

export interface CartApiErrorResponse {
  error?: string;
  retryable?: boolean;
  tokenExpired?: boolean;
  violations?: unknown[];
}

export interface AddToCartResponse {
  success: boolean;
  message: string;
  error?: string;
  responseData?: CartApiErrorResponse | SyliusOrderResponse;
}

const RETRY_DELAY_MS = 750;
const MAX_RETRIES = 1;

export async function AddToCart(
  productCode: string,
  quantity: number,
  orderState: OrderStateType,
  orderDispatch: React.Dispatch<OrderActions>,
  retries = 0,
  forceRefreshToken = false
): Promise<AddToCartResponse> {

  // Get or refresh the cart token
  const tokenResp = await getOrderToken(orderState, orderDispatch, forceRefreshToken);
  const cartToken = tokenResp?.token;
  if (!cartToken) {
    return { success: false, message: 'Failed to add to cart: No cart token available' };
  }

  const session = await getSession();
  const headers = addAuthorizationHeader(
    { 'Content-Type': 'application/ld+json' },
    orderState?.tokenType === 'customer' ? session?.token : null
  );

  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_SITE_URL}/api/orders/${cartToken}/items`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ productVariant: productCode, quantity }),
      }
    );

    const raw = await res.text();
    const bodyIsJson = res.headers.get('Content-Type')?.includes('json') ?? false;
    const data = bodyIsJson ? JSON.parse(raw) : { message: raw };

    // Success path – valid Sylius order returned
    if (res.ok && isSyliusOrderResponse(data)) {
      const order = convertToOrderState(data, orderState?.tokenType);
      if (order) updateOrder(orderDispatch, order);

      publishCartRefresh(order?.token ?? cartToken);

      return { success: true, message: 'SKU added to the cart successfully', responseData: data };
    }

    // Error path – route returns structured error object
    const apiErr = data as CartApiErrorResponse;
    const userMsg = apiErr.error || res.statusText || 'Unknown error';

    // Retry once on retryable errors
    if (apiErr.retryable && retries < MAX_RETRIES) {
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      return AddToCart(productCode, quantity, orderState, orderDispatch, retries + 1, forceRefreshToken);
    }

    // Refresh token and retry once if the cart token is stale
    if (apiErr.tokenExpired && !forceRefreshToken) {
      logMessage('Cart token appears stale. Refreshing…', 'warning');
      return AddToCart(productCode, quantity, orderState, orderDispatch, retries, true);
    }

    return {
      success: false,
      message: `Failed to add to cart: ${userMsg}`,
      error: userMsg,
      responseData: apiErr,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    logMessage(`Exception in AddToCart: ${msg}`, 'error');
    return { success: false, message: 'Failed to add to cart: Internal client error', error: msg };
  }
}
